import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Image } from '@swyftx/aviary/atoms/Image';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Thumbnail } from '@swyftx/aviary/atoms/Thumbnail';
import { Heading, Body, Utility, BodyLink } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { LoanHealthEducationModal } from '@swyftx/features/loans/components/LoanHealth/EducationModal/LoanHealthEducationModal';

import { useIntercom } from 'react-use-intercom';

import { LoansSection } from '../common/LoansSection';

const LoansHowItWorks: React.FC = () => {
  const { show } = useIntercom();
  const { t } = useTranslation('loans');
  const isXs = useTailwindBreakpoint('xs');
  const cardIds = Array.from({ length: 4 }, (_, i) => i + 1);

  const imageMap: Record<number, string> = {
    1: 'loan_application_illustration',
    2: 'loan_finalised_illustration',
    3: 'loan_monitor_illustration',
    4: 'loan_security_illustration',
  };
  const cardData = cardIds.map((id) => ({
    id,
    title: `HowItWorks.Card${id}.title` as const,
    description: `HowItWorks.Card${id}.description` as const,
    reverse: id % 2 !== 0,
    image: imageMap[id],
  }));

  const loanHealthModal = (
    <LoanHealthEducationModal>
      <Body size='medium' color='secondary'>
        {t('HowItWorks.Card3.descriptionP1')}
        <BodyLink color='secondary' className='inline cursor-pointer underline underline-offset-4'>
          {t('HowItWorks.Card3.underlineText')}
        </BodyLink>
        {t('HowItWorks.Card3.descriptionP2')}
      </Body>
    </LoanHealthEducationModal>
  );

  const cards = cardData.map((card) => (
    <div key={card.id} className='grid w-full max-w-[738px] items-start sm:grid-cols-2 sm:items-center sm:gap-64'>
      {!isXs && (
        <Image className={`order-first w-full rounded-16 ${card.reverse ? 'sm:order-last' : ''}`} image={card.image} />
      )}

      <FlexLayout className='flex-row items-start sm:flex-col' spacing={24}>
        <Thumbnail
          className='h-[32px] w-[32px] shrink-0 rounded-[100px]'
          type='counter'
          count={card.id}
          bgColor='bg-color-background-info-subtle'
          textColor='accent'
        />

        <FlexLayout className='flex-col items-start' spacing={8}>
          <Heading color='primary' size='h5'>
            {t(card.title as unknown as TemplateStringsArray)}
          </Heading>
          {card.id === 3 ? (
            loanHealthModal
          ) : (
            <Body size='medium' color='secondary'>
              {t(card.description as unknown as TemplateStringsArray)}
            </Body>
          )}
        </FlexLayout>
      </FlexLayout>
    </div>
  ));

  return (
    <LoansSection
      header={{
        title: t('HowItWorks.heading.title'),
        description: t('HowItWorks.heading.description'),
      }}
      content={
        <FlexLayout className='w-full flex-col items-center' spacing={isXs ? 32 : 64}>
          {cards}
        </FlexLayout>
      }
      buttonHeading={
        <Heading size='h4' color='primary'>
          {t('HowItWorks.Button.label')}
        </Heading>
      }
      button={
        <Button className='rounded-[100px]' size='lg' onClick={show}>
          {t('HowItWorks.Button.button')}
        </Button>
      }
      footer={
        <Utility variant='caption' color='secondary'>
          {t('PutYourBitcoinToWork.Terms')}
        </Utility>
      }
    />
  );
};

LoansHowItWorks.displayName = 'HowItWorks';

export { LoansHowItWorks };
